import React, { useEffect, useMemo, useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate } from "react-router-dom";
import {
  useOffersListStore,
  selectIsLimitReached,
  selectEffectiveActiveCount,
  selectHasPendingChanges,
  type OfferSummary,
} from "../../store/useOffersListStore";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { NavigationBlocker } from "../../components/common/NavigationBlocker";
import { PageBreadcrumbs } from "../../components/common/PageBreadcrumbs";
import { ListSearchBar } from "../../components/shared/ListSearchBar";
import { SalesBoosterTabBar } from "./SalesBoosterTabBar";
import { ListEmptySearchResults } from "../../components/shared/ListEmptySearchResults";

// ─── Sub-components ───────────────────────────────────────────────────────────

function CounterBanner({
  activeCount,
  activeLimit,
  limitReached,
}: {
  activeCount: number;
  activeLimit: number;
  limitReached: boolean;
}) {
  if (activeLimit === 0) return null;

  return (
    <s-banner tone={limitReached ? "warning" : "info"}>
      <s-stack direction="block" gap="small-200">
        <s-text type="strong">
          {limitReached
            ? `Has alcanzado el máximo de ${activeLimit} upsells activos.`
            : `Tienes ${activeCount} de ${activeLimit} upsells activos.`}
        </s-text>
        {limitReached && (
          <s-text>
            Desactiva uno existente para crear o activar otro upsell.
          </s-text>
        )}
      </s-stack>
    </s-banner>
  );
}

function PlacementBadge({
  placement,
}: {
  placement: OfferSummary["placement"];
}) {
  if (!placement) return null;
  const label = placement === "after_complete" ? "Post-compra" : "Pre-compra";
  const classes =
    placement === "after_complete"
      ? "bg-indigo-50 text-indigo-700 border-indigo-200"
      : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${classes}`}
    >
      {label}
    </span>
  );
}

function UpsellsTable({
  upsells,
  allUpsellsCount,
  hasPendingChanges,
  searchQuery,
  onSearchChange,
  onEdit,
  onAdd,
  onToggleActive,
  onDelete,
  onDuplicate,
  addDisabled,
  limitReached,
}: {
  upsells: OfferSummary[];
  allUpsellsCount: number;
  hasPendingChanges: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onEdit: (id: string) => void;
  onAdd: () => void;
  onToggleActive: (id: string, next: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  addDisabled: boolean;
  limitReached: boolean;
}) {
  // Empty state: no offers exist at all on the server.
  if (allUpsellsCount === 0) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="cart-up" />
          <s-text color="subdued">Aún no tienes upsells creados.</s-text>
          <s-button
            variant="primary"
            icon="plus"
            onClick={onAdd}
            disabled={addDisabled}
          >
            Crear tu primer upsell
          </s-button>
        </s-stack>
      </s-box>
    );
  }

  // Empty state: offers exist but all are staged for deletion.
  // Same visual treatment as the true-empty state, different copy.
  if (upsells.length === 0 && !searchQuery && hasPendingChanges) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="cart-up" />
          <s-text color="subdued">
            Todos los upsells están marcados para eliminar.
          </s-text>
          <s-text color="subdued">
            Guarda para confirmar o descarta los cambios para restaurarlos.
          </s-text>
        </s-stack>
      </s-box>
    );
  }

  return (
    <>
      <ListSearchBar value={searchQuery} onChange={onSearchChange} />

      {upsells.length === 0 ? (
        <ListEmptySearchResults onClear={() => onSearchChange("")}>
          Ningún upsell coincide con tu búsqueda.
        </ListEmptySearchResults>
      ) : (
        <s-table>
          <s-table-header-row>
            <s-table-header>Activo</s-table-header>
            <s-table-header>Nombre</s-table-header>
            <s-table-header>Ubicación</s-table-header>
            <s-table-header>Acciones</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {upsells.map((upsell) => {
              // A switch can always be turned off. Turning it on is blocked
              // when the activation would exceed the active-modal limit.
              const switchDisabled =
                !upsell.is_active &&
                limitReached &&
                upsell.display_type === "modal";
              return (
                <s-table-row key={upsell.id}>
                  <s-table-cell>
                    <s-switch
                      checked={upsell.is_active}
                      disabled={switchDisabled}
                      labelAccessibilityVisibility="exclusive"
                      onChange={(e: CallbackEvent<"s-switch">) =>
                        onToggleActive(upsell.id, e.currentTarget.checked)
                      }
                    />
                  </s-table-cell>
                  <s-table-cell>{upsell.internal_name}</s-table-cell>
                  <s-table-cell>
                    <PlacementBadge placement={upsell.placement} />
                  </s-table-cell>
                  <s-table-cell>
                    <s-stack direction="inline" gap="small-200">
                      <s-button
                        variant="tertiary"
                        icon="edit"
                        onClick={() => onEdit(upsell.id)}
                      />
                      <s-button
                        variant="tertiary"
                        icon="duplicate"
                        onClick={() => onDuplicate(upsell.id)}
                      />
                      <s-button
                        variant="tertiary"
                        icon="delete"
                        tone="critical"
                        onClick={() => onDelete(upsell.id)}
                      />
                    </s-stack>
                  </s-table-cell>
                </s-table-row>
              );
            })}
          </s-table-body>
        </s-table>
      )}

      {/* TODO: Stats footer hidden until analytics backend is wired up. The
            numbers above were hardcoded zeros — re-enable once impressions /
            conversion / revenue are tracked end-to-end. */}
      {/* <s-stack direction="inline" gap="base" alignItems="center">
          <s-icon type="clock" />
          <s-text color="subdued">
            <strong>ÚLTIMOS 30 DÍAS:</strong>&nbsp; 0 Visualizaciones &nbsp; 0
            Ventas &nbsp; 0.00% Tasa de conversión &nbsp; 0.00 Ingresos
          </s-text>
        </s-stack> */}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const UpsellsOneClickPage: React.FC = () => {
  const navigate = useNavigate();

  // Gate by kind so we don't briefly render rows from another list
  // page on the first paint after navigation. See UpsellsOneTickPage
  // for the full explanation.
  const offers = useOffersListStore((s) =>
    s.query.kind === "one_click" ? s.offers : []
  );
  const pendingDeleteIds = useOffersListStore((s) => s.pendingDeleteIds);
  const activeLimit = useOffersListStore((s) => s.activeLimit);
  const isLoading = useOffersListStore((s) => s.isLoading);
  const isCommitting = useOffersListStore((s) => s.isCommitting);
  const error = useOffersListStore((s) => s.error);
  const isAwaitingOurKind = useOffersListStore(
    (s) => s.query.kind !== "one_click" || s.lastFetchedAt === null
  );
  const loadOffers = useOffersListStore((s) => s.loadOffers);
  const toggleActive = useOffersListStore((s) => s.toggleActive);
  const duplicateOffer = useOffersListStore((s) => s.duplicateOffer);
  const stageDelete = useOffersListStore((s) => s.stageDelete);
  const commitPending = useOffersListStore((s) => s.commitPending);
  const discardPending = useOffersListStore((s) => s.discardPending);

  const hasPendingChanges = useOffersListStore(selectHasPendingChanges);
  const effectiveActiveCount = useOffersListStore(selectEffectiveActiveCount);
  const limitReached = useOffersListStore(selectIsLimitReached);

  // Derive visible offers here (not via a store selector) so we don't return
  // a fresh array reference on every getSnapshot call, which would cause
  // useSyncExternalStore to loop.
  const visibleOffers = useMemo(() => {
    if (pendingDeleteIds.length === 0) return offers;
    const pending = new Set(pendingDeleteIds);
    return offers.filter((o) => !pending.has(o.id));
  }, [offers, pendingDeleteIds]);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadOffers({ kind: "one_click" });
  }, [loadOffers]);

  const searchedOffers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleOffers;
    return visibleOffers.filter((o) =>
      o.internal_name.toLowerCase().includes(q)
    );
  }, [visibleOffers, searchQuery]);

  const handleEdit = (id: string) =>
    navigate(`/sales-booster/upsells-1-click/${id}`);
  const handleAdd = () => navigate("/sales-booster/upsells-1-click/new");

  const handleToggleActive = async (id: string, next: boolean) => {
    try {
      await toggleActive(id, next);
      window.shopify?.toast?.show(
        next ? "Upsell activado" : "Upsell desactivado"
      );
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudo cambiar el estado del upsell";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  // Staged delete: no API call, no confirmation modal. The Shopify save bar
  // appears as soon as there's at least one pending-delete, and Guardar /
  // Descartar commits or reverts the batch.
  const handleDelete = (id: string) => stageDelete(id);

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateOffer(id);
      window.shopify?.toast?.show(
        "Upsell duplicado como borrador inactivo. Revísalo y actívalo cuando estés listo."
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "No se pudo duplicar el upsell";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  useShopifySaveBar({
    isDirty: hasPendingChanges,
    isSaving: isCommitting,
    onSave: async () => {
      await commitPending();
    },
    onDiscard: discardPending,
  });

  const showInitialLoading = isLoading || isAwaitingOurKind;

  return (
    <div>
      <NavigationBlocker isDirty={hasPendingChanges} />

      <s-page heading="Upsells 1-Click">
        <PageBreadcrumbs
          items={[{ label: "Impulsor de ventas", to: "/sales-booster" }]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          <CounterBanner
            activeCount={effectiveActiveCount}
            activeLimit={activeLimit}
            limitReached={limitReached}
          />

          {error && !showInitialLoading && (
            <s-banner tone="critical">
              <s-text>{error}</s-text>
            </s-banner>
          )}

          {/* Header + add button */}
          <s-stack
            direction="inline"
            alignItems="center"
            justifyContent="space-between"
          >
            <s-box paddingBlockEnd="small-200">
              <s-heading>
                <strong>Tus upsells</strong>
              </s-heading>
            </s-box>
            <s-button
              variant="primary"
              icon="plus"
              onClick={handleAdd}
              disabled={limitReached}
            >
              Añadir upsell
            </s-button>
          </s-stack>

          {showInitialLoading ? (
            <s-box
              background="base"
              border="small subdued solid"
              borderRadius="large-100"
              padding="large-400"
            >
              <s-stack direction="block" gap="base" alignItems="center">
                <s-text color="subdued">Cargando upsells…</s-text>
              </s-stack>
            </s-box>
          ) : (
            <s-section padding="none">
              <UpsellsTable
                upsells={searchedOffers}
                allUpsellsCount={offers.length}
                hasPendingChanges={hasPendingChanges}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onEdit={handleEdit}
                onAdd={handleAdd}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                addDisabled={limitReached}
                limitReached={limitReached}
              />
            </s-section>
          )}
        </s-stack>
      </s-page>
    </div>
  );
};

export default UpsellsOneClickPage;
